import React, { useState } from 'react';
import { 
  GlobeAltIcon, 
  MapIcon,
  ChartBarSquareIcon,
  BeakerIcon,
  CpuChipIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  UserGroupIcon,
  DocumentTextIcon,
  LightBulbIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleCount, setVisibleCount] = useState(6);

  // Full project data extracted from the company profile document (all 14 projects)
  const allProjects = [
    {
      id: 1,
      title: "Detailed Feasibility Study (DFS) in Support of Agricultural Growth Corridors for MCC Malawi Compact II",
      period: "2021-2022",
      client: "Millennium Challenge Corporation (subcontracted by L Gravam Consultants)",
      location: "Nkhatabay, Mzimba, Rumphi, Chitipa, Karonga",
      category: "Feasibility Study",
      description: "Completed various agricultural analysis leading to identification of four corridor roads to be upgraded by the MCC.",
      fullDescription: "The assignment involved comprehensive agricultural analysis, infrastructure assessment, and identification of strategic corridor roads for upgrading. The team conducted detailed feasibility studies including socio-economic impact assessments, agricultural potential mapping, and infrastructure prioritization. The final recommendations directly influenced MCC's investment strategy for agricultural transformation in Malawi.",
      icon: <MapIcon className="w-8 h-8" />
    },
    {
      id: 2,
      title: "Endline Study for Improving Access to Water and Hygiene",
      period: "February 2020",
      client: "Habitat for Humanity",
      location: "Mtsiriza & Mtandire Townships, Lilongwe District",
      category: "WASH / Research",
      description: "Implemented an endline study targeting households and primary schools. Collected WASH baseline information using Kobol Collect tool kit.",
      fullDescription: "The study evaluated the impact of water and hygiene interventions in urban informal settlements. Using digital data collection tools (Kobol Collect), the team gathered quantitative and qualitative data from households, schools, and community health structures. The final report provided actionable recommendations for sustained WASH improvements and guided Habitat for Humanity's subsequent programming.",
      icon: <BeakerIcon className="w-8 h-8" />
    },
    {
      id: 3,
      title: "IFAD PRIDE Feasibility Studies of Irrigation Schemes",
      period: "March-June 2020 / Dec 2020",
      client: "IFAD (subcontracted by L Gravam Consultants)",
      location: "Nkhatabay, Mzimba, Rumphi, Chitipa, Karonga",
      category: "Irrigation",
      description: "Technical lead on establishment and strengthening of cooperative models in Producer Organisations. Assessed existing beneficiary CBOs for scheme management capacities.",
      fullDescription: "A comprehensive feasibility study covering five districts in Northern Malawi. Key responsibilities included: technical leadership for cooperative model establishment, analysis of PPP options for farmer organizations, capacity assessment of existing CBOs for irrigation scheme management, review of agricultural production data, reconnaissance field surveys for agronomic practices, and participatory selection of crop enterprises.",
      icon: <CloudArrowUpIcon className="w-8 h-8" />
    },
    {
      id: 4,
      title: "Smallholder Value Addition Project (SIVAP) - Project Completion Report",
      period: "December 2019",
      client: "African Development Bank / Ministry of Agriculture",
      location: "Malawi (Multiple Districts)",
      category: "M&E",
      description: "Used M&E data and lesson learning sessions to drive continuous improvement. Carried out socio-economic evaluation and preparation of Project Completion Report.",
      fullDescription: "The assignment involved comprehensive evaluation of SIVAP's effectiveness, efficiency, sustainability and impact. The team held discussions with irrigation scheme associations and cooperatives, conducted capacity assessments of institutions, and analyzed results framework indicators. The completion report provided critical lessons for AfDB's future agricultural investments in Malawi.",
      icon: <ChartBarSquareIcon className="w-8 h-8" />
    },
    {
      id: 5,
      title: "Needs Assessment for Improving WASH Status in Rural Communities",
      period: "August 2019",
      client: "Habitat for Humanity",
      location: "TA Kalolo & Masumbankhunda, Lilongwe District",
      category: "Needs Assessment",
      description: "Study targeted rural communities, schools and community health centers. Collected WASH baseline information using Kobol Collect toolkit.",
      fullDescription: "A comprehensive needs assessment covering traditional authorities Kalolo and Masumbankhunda. The team collected WASH baseline data from rural households, educational institutions, and health facilities. Using digital data collection methods, the assessment identified critical gaps in water access, sanitation facilities, and hygiene practices.",
      icon: <BeakerIcon className="w-8 h-8" />
    },
    {
      id: 6,
      title: "ATVET for Women - Baseline Assessment & Training Supervision",
      period: "March-July 2019",
      client: "GFA/CAADP / GIZ",
      location: "Malawi (National)",
      category: "Capacity Building",
      description: "Supported completion of baseline assessment for GIZ supported Agricultural Technical Vocational Education and Training (ATVET) for women.",
      fullDescription: "The baseline assessment involved face-to-face interviews, literature review, report preparation, and stakeholder presentations with ATVET institutions, Ministry of Labour, and TEVETA. Additionally, the role included quality assurance supervision of field trainers, development of monthly training reports, and ensuring adherence to adult learning principles.",
      icon: <CpuChipIcon className="w-8 h-8" />
    },
    {
      id: 7,
      title: "Establishment of Tigwirane Manja Cooperatives",
      period: "July 2018 - June 2019",
      client: "GIZ Green Innovation Centres (GIAE)/NRC",
      location: "Mpingu EPA, Lilongwe District",
      category: "Cooperatives",
      description: "Established five clubs transformed into Tigwirane Manja Cooperatives. Conducted TNA, capacity and institutional building initiatives.",
      fullDescription: "Starting with sensitization meetings, interested farmers were mobilized and registered into five soya clubs. Training Needs Assessment (TNA) was conducted followed by capacity building interventions. The five clubs eventually unified to form Tigwirane Manja Cooperative. The executive committee received comprehensive training on cooperative governance, constitution development, and financial management.",
      icon: <UserGroupIcon className="w-8 h-8" />
    },
    {
      id: 8,
      title: "Thulwe Out Grower Project - Baseline Studies",
      period: "2017",
      client: "Tropha Limited & GIZ Green Innovation Centers",
      location: "Rumphi District",
      category: "Baseline",
      description: "Baseline studies on gender, women and vulnerable groups' participation and landholding. Assessed community organization in scheme management.",
      fullDescription: "The study collected baseline data focusing on gender dynamics, women's participation, and landholding patterns. It assessed existing community organizational structures for scheme management, evaluated land tenure systems, and supported equitable land redistribution aligned with national land tenure policy and legal frameworks.",
      icon: <DocumentTextIcon className="w-8 h-8" />
    },
    {
      id: 9,
      title: "Outgrower Scheme Establishment (Tropha Limited)",
      period: "March-June 2017",
      client: "GIZ Green Innovation Centres (GIAE)",
      location: "Rumphi District",
      category: "Agribusiness",
      description: "Facilitated establishment of outgrower scheme using value chain approach between Tropha Limited and communities.",
      fullDescription: "Built business development capacities and strengthened associations/cooperatives through constitutional development, coaching and training in market research, group management, and conflict resolution. The project established sustainable linkages between a private company and smallholder farmers.",
      icon: <LightBulbIcon className="w-8 h-8" />
    },
    {
      id: 10,
      title: "Breakaway Groups Motivations and Impacts on Associations in Sugar Industry",
      period: "December 2016 - January 2017",
      client: "USAID - United Purpose",
      location: "Malawi (Sugar Industry Regions)",
      category: "Research",
      description: "Research project on operations and dynamics of sugar producing associations and cooperatives.",
      fullDescription: "This research provided critical insights into the operations and dynamics of sugar-producing associations and cooperatives. It examined breakaway group motivations, impacts on parent associations, and identified best practices for business management, capacity development, and institutional strengthening.",
      icon: <ChartBarSquareIcon className="w-8 h-8" />
    },
    {
      id: 11,
      title: "EDULINK Research: Women, Food Security and Education",
      period: "December 2015 - January 2016",
      client: "European Union (ACP-EU Cooperation)",
      location: "All three regions of Malawi",
      category: "Research",
      description: "Studied 10 women business groups across all three regions using qualitative and quantitative methods.",
      fullDescription: "Part of the ACP-EU Cooperation Program in Higher Education, this gender equality research examined the nexus between women's economic empowerment, food security, and education. The study employed mixed methods across 10 women business groups, providing evidence-based recommendations for policy and programming.",
      icon: <UserGroupIcon className="w-8 h-8" />
    },
    {
      id: 12,
      title: "Sugar Capacity Building Project - Training of Trainers",
      period: "September 2013",
      client: "USAID - Concern Universal",
      location: "Malawi (Sugar Growing Regions)",
      category: "Training",
      description: "Built capacity of officers, sugar growing farmers in cooperatives and government officials with skills in training management.",
      fullDescription: "Designed and delivered Training of Trainers (ToT) focused on capacity building for agribusiness cooperatives. The program equipped participants with adult learning methodologies, training cycle management, facilitation techniques, and cooperative governance skills.",
      icon: <CpuChipIcon className="w-8 h-8" />
    },
    {
      id: 13,
      title: "Long Term Impact of Treadle Pump Introduction in Malawi",
      period: "July-October 2010",
      client: "IWMI-Ghana",
      location: "Blantyre & Mchinji Districts",
      category: "Impact Assessment",
      description: "Follow up study of smallholder farmers who benefited from treadle pumps in 2005/2006 season. Assessed livelihoods status.",
      fullDescription: "The longitudinal study evaluated long-term impacts of treadle pump adoption on household livelihoods, income, food security, and asset accumulation, providing valuable insights for small-scale irrigation promotion strategies.",
      icon: <CloudArrowUpIcon className="w-8 h-8" />
    },
    {
      id: 14,
      title: "Analysis of Inclusion and Exclusion of Vulnerable Households in FISP",
      period: "June-December 2010",
      client: "Ministry of Agriculture and FAO",
      location: "Malawi (National)",
      category: "Policy Analysis",
      description: "Assessment of the Farm Input Subsidy Program (FISP) from process and impact aspects using beneficiary criteria.",
      fullDescription: "The study critically examined inclusion/exclusion errors targeting vulnerable groups (female headed households, women farmers, resource poor PLHIV), providing evidence-based recommendations for improving targeting mechanisms and ensuring social protection reach the most marginalized agricultural households.",
      icon: <DocumentTextIcon className="w-8 h-8" />
    }
  ];

  const showMore = () => setVisibleCount(allProjects.length);
  const showLess = () => setVisibleCount(6);

  return (
    <div id="projects" className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        
        {/* --- Header --- */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold text-[#1e3a8a] mb-4 uppercase tracking-tight">
            Our <span className="text-[#22c55e]">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-[#22c55e] mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto italic font-medium">
            "Ensuring quality control and value for money across every assignment."
          </p>
        </div>

        {/* --- Global Impact Banner with updated text --- */}
        <div className="bg-gray-50 border-l-8 border-[#22c55e] p-8 shadow-sm rounded-r-xl flex flex-col md:flex-row items-center gap-8 mb-20">
          <div className="bg-white p-4 rounded-full text-[#1e3a8a] shadow-sm">
            <GlobeAltIcon className="w-12 h-12" />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2 text-left">International Standards, Local Expertise</h3>
            <p className="text-gray-600 leading-relaxed text-left">
              Our team boasts expertise delivered to local projects with international standards including 
              <span className="font-bold text-[#1e3a8a]"> World Bank </span> and 
              <span className="font-bold text-[#1e3a8a]"> African Development Bank </span> funded initiatives. 
              We bridge the gap between global requirements and community realities.
            </p>
          </div>
        </div>

        {/* --- Projects Grid --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {allProjects.slice(0, visibleCount).map((project) => (
            <div 
              key={project.id} 
              className="bg-white rounded-3xl p-8 border border-gray-100 flex flex-col hover:border-[#22c55e] hover:shadow-2xl transition-all duration-500 relative overflow-hidden group"
            >
              {/* Background Icon Decoration */}
              <div className="absolute -right-4 -top-4 text-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-150">
                {project.icon}
              </div>

              <div className="flex justify-between items-start mb-6">
                <div className="text-[#22c55e] bg-green-50 p-3 rounded-xl">
                  {project.icon}
                </div>
                <span className="bg-blue-50 text-[#1e3a8a] text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-wider">
                  {project.category}
                </span>
              </div>
              
              <h4 className="text-xl font-bold text-[#1e3a8a] mb-3 leading-tight min-h-[3.5rem] group-hover:text-[#22c55e] transition-colors line-clamp-2">
                {project.title}
              </h4>
              
              <div className="space-y-2 mb-6">
                <p className="text-gray-400 text-[10px] font-bold uppercase flex items-center gap-2 truncate">
                  <BuildingOffice2Icon className="w-4 h-4 flex-shrink-0" /> 
                  <span className="truncate">{project.client}</span>
                </p>
                <p className="text-gray-400 text-[10px] font-bold uppercase flex items-center gap-2">
                  <CalendarDaysIcon className="w-4 h-4" /> {project.period}
                </p>
              </div>
              
              <p className="text-gray-600 text-sm mb-8 flex-grow line-clamp-3 leading-relaxed">
                {project.description}
              </p>
              
              <button 
                onClick={() => setSelectedProject(project)}
                className="flex items-center justify-center w-full py-3 bg-gray-50 text-[#1e3a8a] font-bold text-xs uppercase rounded-xl hover:bg-[#1e3a8a] hover:text-white transition-all tracking-widest"
              >
                Full Case Study <ChevronRightIcon className="w-4 h-4 ml-2" />
              </button>
            </div>
          ))}
        </div>

        {/* --- Navigation --- */}
        <div className="mt-16 text-center">
          {visibleCount < allProjects.length ? (
            <button 
              onClick={showMore} 
              className="inline-flex items-center gap-2 bg-[#1e3a8a] text-white px-10 py-4 rounded-full font-bold hover:bg-[#22c55e] transition shadow-xl"
            >
              Explore More Experience <ChevronDownIcon className="w-5 h-5" />
            </button>
          ) : (
            allProjects.length > 6 && (
              <button 
                onClick={showLess} 
                className="inline-flex items-center gap-2 border-2 border-[#1e3a8a] text-[#1e3a8a] px-10 py-4 rounded-full font-bold hover:bg-gray-50 transition"
              >
                Show Less <ChevronUpIcon className="w-5 h-5" />
              </button>
            )
          )}
        </div>

        {/* Project Count Indicator */}
        <div className="text-center mt-8 text-sm text-gray-400">
          Showing {Math.min(visibleCount, allProjects.length)} of {allProjects.length} completed assignments
        </div>
      </div>

      {/* --- MODAL (preserved style from your original) --- */}
      {selectedProject && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-blue-950/90 backdrop-blur-sm">
          <div className="bg-white rounded-[40px] max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-12">
            <button 
              onClick={() => setSelectedProject(null)} 
              className="absolute top-8 right-8 text-gray-300 hover:text-red-500 transition-colors"
            >
              <XMarkIcon className="w-10 h-10" />
            </button>

            <div className="text-[#22c55e] mb-4">{selectedProject.icon}</div>
            <h2 className="text-3xl font-black text-[#1e3a8a] mb-8 leading-tight">{selectedProject.title}</h2>
            
            <div className="grid grid-cols-2 gap-6 mb-10">
              <div className="bg-gray-50 p-4 rounded-2xl">
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Client</p>
                <p className="text-sm font-bold text-gray-700">{selectedProject.client}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-2xl">
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Period</p>
                <p className="text-sm font-bold text-gray-700">{selectedProject.period}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-2xl col-span-2">
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Location</p>
                <p className="text-sm font-bold text-gray-700">{selectedProject.location}</p>
              </div>
            </div>

            <div className="bg-blue-50/50 p-8 rounded-3xl border-l-8 border-[#22c55e]">
              <h5 className="font-bold text-[#1e3a8a] mb-3 uppercase text-xs tracking-widest">Technical Outcome</h5>
              <p className="text-gray-700 leading-relaxed font-medium italic">"{selectedProject.fullDescription}"</p>
            </div>
          </div>
        </div>
      )}

      {/* Add line-clamp CSS for truncation */}
      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Projects;